import { Icon } from '@/components/ui/Icon';
import { handleMergePricesData } from '@/lib/service/mergeRecommendPrices';
import type { MergedRecommendPrice, RecommendPrice } from '@/lib/service/types';
import { formatPrice } from '@/lib/utils/formatPrice';

import styles from './PriceTable.module.scss';

export interface PriceTableLabels {
  caption: string;
  category: string;
  maximumPrices: string;
  onEstimate: string;
  other: string;
  packingIncluded: string;
  packingNotIncluded: string;
  price: string;
  service: string;
  type: string;
  vatIncluded: string;
  vatNotIncluded: string;
}

interface PriceTableProps {
  labels: PriceTableLabels;
  locale: string;
  packShipIncluded?: boolean;
  prices: RecommendPrice[];
  vatIncluded?: boolean;
}

function buildOtherGroup(labels: PriceTableLabels): MergedRecommendPrice {
  return {
    title: labels.other,
    description: '',
    items: [{ category1: '', category2: [''], price: [{ amount: null, currency: '' }] }],
  };
}

function ServiceHeading({ group }: Readonly<{ group: MergedRecommendPrice }>): React.ReactNode {
  if (!group.description) {
    return <span className={styles.serviceTitle}>{group.title}</span>;
  }

  return (
    <details className={styles.details}>
      <summary className={styles.summary}>
        <span className={styles.serviceTitle}>{group.title}</span>
        <Icon aria-hidden="true" className={styles.chevron} name="chevron-down" />
      </summary>
      <p className={styles.description}>{group.description}</p>
    </details>
  );
}

function CategoryValues({ values }: Readonly<{ values: string[] }>): React.ReactNode {
  return (
    <span className={styles.values}>
      {values.map((value, index) => (
        <span key={`${value}-${index}`}>{value}</span>
      ))}
    </span>
  );
}

function PriceValues({
  labels,
  locale,
  prices,
}: Readonly<{
  labels: PriceTableLabels;
  locale: string;
  prices: MergedRecommendPrice['items'][number]['price'];
}>): React.ReactNode {
  return (
    <span className={styles.values}>
      {prices.map((priceEntry, priceIndex) => (
        <span key={`${priceEntry.currency}-${priceIndex}`}>
          {priceEntry.amount === null
            ? labels.onEstimate
            : formatPrice(priceEntry.amount, priceEntry.currency, locale)}
        </span>
      ))}
    </span>
  );
}

function SummaryBar({
  labels,
  packShipIncluded,
  vatIncluded,
}: Readonly<{
  labels: PriceTableLabels;
  packShipIncluded: boolean;
  vatIncluded: boolean;
}>): React.ReactNode {
  return (
    <span className={styles.summaryBar}>
      <span>{labels.maximumPrices}</span>
      <span className={styles.summaryNotes}>
        <span>{vatIncluded ? labels.vatIncluded : labels.vatNotIncluded}</span>
        <span>{packShipIncluded ? labels.packingIncluded : labels.packingNotIncluded}</span>
      </span>
    </span>
  );
}

export function PriceTable({
  labels,
  locale,
  packShipIncluded = false,
  prices,
  vatIncluded = false,
}: Readonly<PriceTableProps>): React.ReactNode {
  const groupedPrices = [...handleMergePricesData(prices), buildOtherGroup(labels)];

  return (
    <div className={styles.wrapper}>
      <table className={styles.mobileTable}>
        <caption className={styles.visuallyHidden}>{labels.caption}</caption>
        <thead>
          <tr className={styles.summaryRow}>
            <th colSpan={2}>
              <SummaryBar
                labels={labels}
                packShipIncluded={packShipIncluded}
                vatIncluded={vatIncluded}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {groupedPrices.map((group) => (
            <MobileGroup key={group.title} group={group} labels={labels} locale={locale} />
          ))}
        </tbody>
      </table>

      <table className={styles.desktopTable}>
        <caption className={styles.visuallyHidden}>{labels.caption}</caption>
        <colgroup>
          <col className={styles.serviceColumn} />
          <col className={styles.categoryColumn} />
          <col className={styles.typeColumn} />
          <col className={styles.priceColumn} />
        </colgroup>
        <thead>
          <tr className={styles.summaryRow}>
            <th colSpan={4}>
              <SummaryBar
                labels={labels}
                packShipIncluded={packShipIncluded}
                vatIncluded={vatIncluded}
              />
            </th>
          </tr>
          <tr className={styles.columnHeaders}>
            <th scope="col">
              <span className={styles.visuallyHidden}>{labels.service}</span>
            </th>
            <th scope="col">
              <span className={styles.visuallyHidden}>{labels.category}</span>
            </th>
            <th scope="col">
              <span className={styles.visuallyHidden}>{labels.type}</span>
            </th>
            <th scope="col">
              <span className={styles.visuallyHidden}>{labels.price}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {groupedPrices.map((group) =>
            group.items.map((item, itemIndex) => (
              <tr key={`${group.title}-${item.category1}-${itemIndex}`}>
                {itemIndex === 0 && (
                  <th className={styles.service} rowSpan={group.items.length} scope="row">
                    <ServiceHeading group={group} />
                  </th>
                )}
                <td className={styles.category}>{item.category1}</td>
                <td className={styles.type}>
                  <CategoryValues values={item.category2} />
                </td>
                <td className={styles.prices}>
                  <PriceValues labels={labels} locale={locale} prices={item.price} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function MobileGroup({
  group,
  labels,
  locale,
}: Readonly<{
  group: MergedRecommendPrice;
  labels: PriceTableLabels;
  locale: string;
}>): React.ReactNode {
  return (
    <>
      <tr>
        <th className={styles.mobileService} colSpan={2} scope="colgroup">
          <ServiceHeading group={group} />
        </th>
      </tr>
      {group.items.map((item) => {
        const hasCategory2 = item.category2.some(Boolean);

        if (!hasCategory2) {
          return (
            <tr key={`${group.title}-${item.category1}`}>
              <td className={styles.category}>{item.category1}</td>
              <td className={styles.prices}>
                <PriceValues labels={labels} locale={locale} prices={item.price} />
              </td>
            </tr>
          );
        }

        return (
          <MobileItemRows
            key={`${group.title}-${item.category1}`}
            item={item}
            labels={labels}
            locale={locale}
          />
        );
      })}
    </>
  );
}

function MobileItemRows({
  item,
  labels,
  locale,
}: Readonly<{
  item: MergedRecommendPrice['items'][number];
  labels: PriceTableLabels;
  locale: string;
}>): React.ReactNode {
  return (
    <>
      <tr>
        <td className={styles.category} colSpan={2}>
          {item.category1}
        </td>
      </tr>
      <tr>
        <td className={styles.type}>
          <CategoryValues values={item.category2} />
        </td>
        <td className={styles.prices}>
          <PriceValues labels={labels} locale={locale} prices={item.price} />
        </td>
      </tr>
    </>
  );
}
